import { Button, Checkbox, Input, Modal, NativeSelect } from '@immo/ui-kit';
import { FLEET_ENGINE_NAMES } from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { useCommandVerbCopy, type ConfirmableVerb } from './command-verbs';
import {
  ANY_ENGINE,
  EMPTY_ENGINE_FORM,
  engineFormErrors,
  isEngineFormValid,
  type EngineChoice,
  type EngineForm,
} from './engine-form';

type Props = {
  readonly verb: ConfirmableVerb;
  readonly machineName: string;
  /** `null` for a host verb — the scope line then names the machine alone. */
  readonly profile: string | null;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onConfirm: () => void;
  readonly onClose: () => void;
  /** Only read for `verb === 'engine'` — the lane, an optional project and the engine to pin. */
  readonly engineForm?: EngineForm;
  readonly onEngineFormChange?: (patch: Partial<EngineForm>) => void;
  /** Only read for `update-agent` / `force-update-agent` — ticking the box swaps one verb for the other. */
  readonly onForceUpdateChange?: (force: boolean) => void;
  /** Only read for `stop` / `stop-all` (fleet-agent ADR 0013) — stop after the round in hand. */
  readonly finish?: boolean;
  readonly onFinishChange?: (finish: boolean) => void;
};

/**
 * One confirm, driven by the verb prop (BL-112's ApprovalDialog precedent) —
 * each verb's title, body and CTA come from `command-verbs.ts`, so the
 * footer button and this dialog never drift on copy. The scope line is the
 * one question asked before an irreversible verb: which machine, which
 * profile — and for a host verb, which machine, full stop.
 *
 * `engine` pins a lane (and optionally one project of it) to an engine, or
 * back to "any". The same rule: the CTA waits for a form the contract
 * accepts. Whether the host can run it (lane declared, CLI installed) only the
 * host knows; it answers `refused` with the reason, shown in the feed.
 *
 * `update-agent` carries a "discard local changes" box: ticked, the dialog
 * becomes `force-update-agent` — its copy, its CTA, its verb on the wire.
 *
 * `stop` and `stop-all` carry a "finish the round first" box (fleet-agent ADR
 * 0013): ticked, the host stops after the round in hand rather than at the
 * next step boundary.
 */
export function CommandDialog({
  verb,
  machineName,
  profile,
  busy,
  error,
  onConfirm,
  onClose,
  engineForm = EMPTY_ENGINE_FORM,
  onEngineFormChange,
  onForceUpdateChange,
  finish = false,
  onFinishChange,
}: Props) {
  const t = useTranslations('pages.admin.fleet');
  const copy = useCommandVerbCopy(verb);
  const isEngine = verb === 'engine';
  const engineInvalid = isEngine && !isEngineFormValid(engineForm);
  const isUpdate = verb === 'update-agent' || verb === 'force-update-agent';
  const isStop = verb === 'stop' || verb === 'stop-all';

  return (
    <Modal
      open
      onClose={() => (busy ? undefined : onClose())}
      confirmOnClose={false}
      size="sm"
      title={copy.title}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose} disabled={busy}>
            {t('cancel-cta')}
          </Button>
          <Button type="button" variant={copy.variant} onClick={onConfirm} disabled={busy || engineInvalid}>
            {copy.cta}
          </Button>
        </>
      }
    >
      <p className="font-mono text-base-sm text-text-secondary">
        {profile === null ? machineName : t('scope-line', { machine: machineName, profile })}
      </p>
      <p className="mt-3 text-sm text-text-secondary">{copy.body}</p>

      {isUpdate && (
        <label className="mt-4 flex items-start gap-2 text-sm text-text-primary">
          <Checkbox
            checked={verb === 'force-update-agent'}
            onCheckedChange={(value) => onForceUpdateChange?.(value)}
            disabled={busy}
            aria-label={t('force-update-toggle')}
          />
          {t('force-update-toggle')}
        </label>
      )}

      {isStop && (
        <label className="mt-4 flex items-start gap-2 text-sm text-text-primary">
          <Checkbox
            checked={finish}
            onCheckedChange={(value) => onFinishChange?.(value)}
            disabled={busy}
            aria-label={t('finish-toggle')}
          />
          {t('finish-toggle')}
        </label>
      )}

      {isEngine && <EngineFields form={engineForm} busy={busy} onChange={(patch) => onEngineFormChange?.(patch)} />}

      {error !== null && (
        <p role="alert" className="mt-3 text-sm text-danger-600">
          {error}
        </p>
      )}
    </Modal>
  );
}

function EngineFields({
  form,
  busy,
  onChange,
}: {
  readonly form: EngineForm;
  readonly busy: boolean;
  readonly onChange: (patch: Partial<EngineForm>) => void;
}) {
  const t = useTranslations('pages.admin.fleet.engine');
  const errors = engineFormErrors(form);
  const laneTyped = form.lane.trim() !== '';

  return (
    <div className="mt-4 flex flex-col gap-3">
      <Input
        className="font-mono text-base-sm"
        value={form.lane}
        onChange={(event) => onChange({ lane: event.target.value })}
        disabled={busy}
        invalid={errors.lane}
        aria-label={t('lane-field')}
      />
      <Input
        className="font-mono text-base-sm"
        value={form.project}
        onChange={(event) => onChange({ project: event.target.value })}
        disabled={busy || !laneTyped}
        invalid={errors.project}
        aria-label={t('project-field')}
        placeholder={t('project-placeholder')}
      />
      <NativeSelect
        value={form.engine}
        onChange={(value) => onChange({ engine: value as EngineChoice })}
        disabled={busy}
        aria-label={t('engine-field')}
        options={[ANY_ENGINE, ...FLEET_ENGINE_NAMES].map((engine) => ({
          value: engine,
          label: engine === ANY_ENGINE ? t('any') : engine,
        }))}
      />
    </div>
  );
}
